// askSmart-ui/src/pages/TOSP/components/WastePsfCalculatorPanel.tsx
import React, { useState } from "react";
import { formatCurrency, formatSqFt } from "../format";
import { COMMERCIAL_SPACE_TYPE, isOccupancyActive, Tenant, Unit } from "../types";
import { parseNumeric, sameText } from "../validation";

interface WastePsfCalculatorPanelProps {
  /** The committed Waste Removal Types — one row each. */
  wasteTypes: string[];
  units: Unit[];
  tenants: Tenant[];
  /** The cost being divided across the bands. */
  totalCost: number | null;
  onTotalCostChange: (value: number | null) => void;
  /** Each band's share of that cost, keyed by waste type. */
  percentages: Record<string, number>;
  onPercentagesChange: (percentages: Record<string, number>) => void;
}

const GRID_COLUMNS = "1.3fr 0.9fr 0.7fr 1fr 0.8fr";

// The commercial floor area sitting behind each waste band. Read straight off
// the tenants and their units rather than entered — a unit's area is already
// recorded once, and a figure typed here would only go stale.
//
// A unit counts when it's Commercial and currently occupied by a tenant in
// that band. Ended occupancies don't count: the area belongs to whoever holds
// the unit now.
function commercialSqFtByWasteType(
  wasteTypes: string[],
  units: Unit[],
  tenants: Tenant[],
): Record<string, number> {
  const totals: Record<string, number> = Object.fromEntries(
    wasteTypes.map((type) => [type, 0]),
  );

  tenants.forEach((tenant) => {
    const band = wasteTypes.find((type) => sameText(type, tenant.wasteRemoval));
    if (!band) return;
    (tenant.occupancies || []).forEach((occupancy) => {
      if (!isOccupancyActive(occupancy)) return;
      // One lease may demise several spaces; each contributes its own unit's
      // area, and only the commercial ones count towards the waste bands.
      (occupancy.leasedSpaces || []).forEach((space) => {
        const unit = space.unitId ? units.find((u) => u.id === space.unitId) : undefined;
        if (!unit || !sameText(unit.spaceType, COMMERCIAL_SPACE_TYPE)) return;
        totals[band] += unit.occupancySqFt || 0;
      });
    });
  });

  return totals;
}

// Waste PSF Calculator — area per band is calculated from the units; the share
// of the cost and the cost itself are entered. Cost per band and per square
// foot follow from the two.
export default function WastePsfCalculatorPanel({
  wasteTypes,
  units,
  tenants,
  totalCost,
  onTotalCostChange,
  percentages,
  onPercentagesChange,
}: WastePsfCalculatorPanelProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  // Held as text while typing so a half-entered figure isn't parsed away.
  const [costText, setCostText] = useState<string | null>(null);

  const sqFt = commercialSqFtByWasteType(wasteTypes, units, tenants);
  const totalSqFt = Object.values(sqFt).reduce((sum, value) => sum + value, 0);
  const percentTotal = wasteTypes.reduce((sum, type) => sum + (percentages[type] ?? 0), 0);
  const percentOff = wasteTypes.length > 0 && Math.abs(percentTotal - 100) >= 0.01;

  const costFor = (type: string): number | null => {
    if (totalCost === null || percentages[type] === undefined) return null;
    return (totalCost * percentages[type]) / 100;
  };

  const perSqFtFor = (type: string): number | null => {
    const cost = costFor(type);
    if (cost === null || !sqFt[type]) return null;
    return cost / sqFt[type];
  };

  const setPercentage = (type: string, raw: string) => {
    const next = { ...percentages };
    const parsed = parseNumeric(raw);
    if (!raw.trim() || parsed === null) delete next[type];
    else next[type] = parsed;
    onPercentagesChange(next);
  };

  const allocated = wasteTypes.reduce((sum, type) => sum + (costFor(type) ?? 0), 0);

  return (
    <section className="tosp-config-panel">
      <div className="tosp-config-header">
        <h3 className="tosp-config-title">Waste PSF Calculator</h3>
        <button
          type="button"
          className="tosp-help-toggle"
          aria-expanded={helpOpen}
          title="What's this?"
          onClick={() => setHelpOpen((open) => !open)}
        >
          ?
        </button>
      </div>

      {helpOpen && (
        <p className="tosp-config-note">
          Enter the total cost and each Waste Removal Type's share; to calculate rate psf.
        </p>
      )}

      <div className="tosp-field" style={{ marginBottom: 12 }}>
        <label className="tosp-label" htmlFor="tosp-waste-total-cost">
          Total Waste Cost
        </label>
        <div className="tosp-affix-wrap">
          <span className="tosp-affix tosp-affix-prefix">$</span>
          <input
            id="tosp-waste-total-cost"
            className="manage-input tosp-has-prefix"
            autoComplete="off"
            inputMode="decimal"
            value={costText ?? (totalCost === null ? "" : String(totalCost))}
            onChange={(e) => setCostText(e.target.value)}
            onBlur={(e) => {
              const raw = e.target.value;
              onTotalCostChange(raw.trim() ? parseNumeric(raw) : null);
              setCostText(null);
            }}
          />
        </div>
      </div>

      <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
        <div>Waste Category</div>
        <div>SqFt</div>
        <div>%</div>
        <div>Cost</div>
        <div>Per SF</div>
      </div>

      {wasteTypes.length === 0 && (
        <div className="tosp-grid-empty">
          No waste removal types yet — add them in the panel above.
        </div>
      )}

      {wasteTypes.map((type) => {
        const cost = costFor(type);
        const perSqFt = perSqFtFor(type);
        return (
          <div key={type} className="tosp-grid-row" style={{ gridTemplateColumns: GRID_COLUMNS }}>
            <div className="tosp-cell-wrap">{type}</div>
            <div>{formatSqFt(sqFt[type] || 0)}</div>
            <div className="tosp-affix-wrap">
              <input
                className="manage-input tosp-has-suffix tosp-percent-input"
                autoComplete="off"
                inputMode="decimal"
                aria-label={`Share of waste cost for ${type}`}
                value={percentages[type] ?? ""}
                onChange={(e) => setPercentage(type, e.target.value)}
              />
              <span className="tosp-affix tosp-affix-suffix">%</span>
            </div>
            <div>{cost === null ? "—" : formatCurrency(cost)}</div>
            <div>{perSqFt === null ? "—" : formatCurrency(perSqFt)}</div>
          </div>
        );
      })}

      {wasteTypes.length > 0 && (
        <div
          className="tosp-grid-row tosp-grid-total"
          style={{ gridTemplateColumns: GRID_COLUMNS }}
        >
          <div>Total</div>
          <div>{formatSqFt(totalSqFt)}</div>
          {/* Amber when the shares don't account for the whole cost. */}
          <div className={percentOff ? "tosp-percent-off" : undefined}>
            {Number(percentTotal.toFixed(2))}%
          </div>
          <div>{totalCost === null ? "—" : formatCurrency(allocated)}</div>
          <div>{totalSqFt > 0 && totalCost !== null ? formatCurrency(allocated / totalSqFt) : "—"}</div>
        </div>
      )}

      {percentOff && (
        <p className="tosp-field-hint" style={{ marginTop: 8 }}>
          Shares total {Number(percentTotal.toFixed(2))}% — {formatCurrency(
            totalCost === null ? 0 : totalCost - allocated,
          )}{" "}
          of the cost is unallocated.
        </p>
      )}
    </section>
  );
}
